import { gql } from '@apollo/client';

const FITNESS_WORKOUT_FIELDS = `
  id
  title
  mode
  workoutType
  workoutDate
  scheduledAt
  completedAt
  activityType
  durationMinutes
  intensity
  caloriesBurned
  distance
  distanceUnit
  pace
  averageHeartRate
  maxHeartRate
  elevationGain
  equipment
  muscleGroups
  strengthExercises {
    id
    name
    muscleGroup
    equipment
    category
    trackingMode
    weightUnit
    isCustom
    notes
    sets {
      id
      reps
      weight
      durationSeconds
      distance
      restSeconds
      completed
    }
  }
  focusAreas
  className
  location
  coach
  status
  plannedDate
  completedDate
  planId
  planDayId
  planName
  templateName
  energyLevel
  sorenessLevel
  recoveryNotes
  rating
  review
  notes
  source
  sourceExternalId
  sourceDevice
  sourceSyncedAt
  tags
  favorite
  createdAt
  updatedAt
`;

const FITNESS_WORKOUT_LIST_FIELDS = `
  id
  title
  mode
  workoutType
  workoutDate
  scheduledAt
  completedAt
  activityType
  durationMinutes
  intensity
  distance
  distanceUnit
  muscleGroups
  status
  plannedDate
  completedDate
  planId
  planDayId
  planName
  templateName
  source
  sourceExternalId
  sourceDevice
  sourceSyncedAt
  tags
  favorite
  updatedAt
`;

export const GET_FITNESS_WORKOUTS = gql`
  query GetFitnessWorkouts($first: Int, $after: String) {
    getFitnessWorkoutsListPage(first: $first, after: $after) {
      nodes {
        ${FITNESS_WORKOUT_LIST_FIELDS}
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_FITNESS_WORKOUT = gql`
  query GetFitnessWorkout($id: ID!) {
    getFitnessWorkout(id: $id) {
      ${FITNESS_WORKOUT_FIELDS}
    }
  }
`;

export const ADD_FITNESS_WORKOUT = gql`
  mutation AddFitnessWorkout($input: FitnessWorkoutInput!) {
    addFitnessWorkout(input: $input) {
      ${FITNESS_WORKOUT_FIELDS}
    }
  }
`;

export const UPDATE_FITNESS_WORKOUT = gql`
  mutation UpdateFitnessWorkout($id: ID!, $input: FitnessWorkoutInput!) {
    updateFitnessWorkout(id: $id, input: $input) {
      ${FITNESS_WORKOUT_FIELDS}
    }
  }
`;

export const DELETE_FITNESS_WORKOUT = gql`
  mutation DeleteFitnessWorkout($id: ID!) {
    deleteFitnessWorkout(id: $id)
  }
`;

const FITNESS_PLAN_FIELDS = `
  id title goal daysPerWeek startDate endDate status
  days {
    id weekday savedWorkoutId title notes
    strengthExercises {
      id name muscleGroup equipment category trackingMode weightUnit isCustom notes
      sets { id reps weight durationSeconds distance restSeconds completed }
    }
  }
  notes favorite createdAt updatedAt
`;

export const GET_FITNESS_PLANS = gql`
  query GetFitnessPlans { getFitnessPlans { ${FITNESS_PLAN_FIELDS} } }
`;

export const GET_FITNESS_ROUTINE_HISTORY = gql`
  query GetFitnessRoutineHistory($planId: ID!, $first: Int, $after: String) {
    getFitnessRoutineHistory(planId: $planId, first: $first, after: $after) {
      nodes {
        id planId planDayId title completedAt workoutDate durationMinutes status source
        exerciseCount setCount updatedAt
      }
      pageInfo { hasNextPage endCursor }
    }
  }
`;

export const GET_FITNESS_EXERCISE_CATALOG = gql`
  query GetFitnessExerciseCatalog {
    getFitnessExerciseCatalog {
      id name muscleGroup equipment category trackingMode weightUnit isCustom
      lastReps lastWeight lastDurationSeconds lastDistance lastUsedAt
    }
  }
`;

export const ADD_FITNESS_PLAN = gql`
  mutation AddFitnessPlan($input: FitnessPlanInput!) {
    addFitnessPlan(input: $input) { ${FITNESS_PLAN_FIELDS} }
  }
`;

export const UPDATE_FITNESS_PLAN = gql`
  mutation UpdateFitnessPlan($id: ID!, $input: FitnessPlanInput!) {
    updateFitnessPlan(id: $id, input: $input) { ${FITNESS_PLAN_FIELDS} }
  }
`;

export const DELETE_FITNESS_PLAN = gql`
  mutation DeleteFitnessPlan($id: ID!) { deleteFitnessPlan(id: $id) }
`;

export const START_FITNESS_WORKOUT = gql`
  mutation StartFitnessWorkout($planId: ID!, $planDayId: ID) {
    startFitnessWorkout(planId: $planId, planDayId: $planDayId) { ${FITNESS_WORKOUT_FIELDS} }
  }
`;

export const LOG_FITNESS_ROUTINE_WORKOUT = gql`
  mutation LogFitnessRoutineWorkout($planId: ID!, $planDayId: ID!, $completedDate: String!) {
    logFitnessRoutineWorkout(planId: $planId, planDayId: $planDayId, completedDate: $completedDate) { ${FITNESS_WORKOUT_FIELDS} }
  }
`;

export const UPDATE_FITNESS_WORKOUT_SESSION = gql`
  mutation UpdateFitnessWorkoutSession($id: ID!, $input: FitnessWorkoutSessionInput!) {
    updateFitnessWorkoutSession(id: $id, input: $input) { ${FITNESS_WORKOUT_FIELDS} }
  }
`;

export const FINISH_FITNESS_WORKOUT = gql`
  mutation FinishFitnessWorkout($id: ID!, $input: FitnessWorkoutSessionInput!) {
    finishFitnessWorkout(id: $id, input: $input) { ${FITNESS_WORKOUT_FIELDS} }
  }
`;

export const DISCARD_FITNESS_WORKOUT = gql`
  mutation DiscardFitnessWorkout($id: ID!) { discardFitnessWorkout(id: $id) }
`;
